import { useState, useCallback, useEffect } from "react";
import { BuildingId, EcsBuilding } from 'shared/models';
import { callApi } from "apiClient";
import { useTranslation } from "react-i18next";
import { toError } from 'shared/util/toError';

export function useEcsBuilding(buildingId: BuildingId | null) {
    const { t } = useTranslation();

    const [ data, setData ] = useState<EcsBuilding | null>();
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isSaving, setIsSaving ] = useState(false);
    const [ errorMessage, setErrorMessage ] = useState<string>();
    const [ successVisible, setSuccessVisible ] = useState(false);

    const hideSuccess = useCallback(() => {
        setSuccessVisible(false);
    }, [ setSuccessVisible ]);

    const saveEcsBuilding = useCallback(async (data: Omit<EcsBuilding, 'lastUpdated'>) => {
        if (!buildingId) {
            return;
        }
        setIsSaving(true);
        setSuccessVisible(false);
        setErrorMessage('');
        try {
            delete (data as any).lastUpdated;
            const savedData = await callApi('PUT', `/api/buildings/${buildingId}/ecs`, data);
            setData(savedData);
            setSuccessVisible(true);
        }
        catch (e) {
            const err = toError(e);
            setErrorMessage(err.message);
        }
        finally {
            setIsSaving(false);
        }
    }, [ setData, buildingId, setIsSaving, setErrorMessage, setSuccessVisible ]);

    useEffect(() => {
        if (buildingId && !isLoading && typeof data === 'undefined' && !errorMessage) {
            setIsLoading(true);
            void (async () => {
                try {
                    const cacheBust = Math.random().toString(28).substr(2);
                    const ecsBuilding = await callApi('GET', `/api/buildings/${buildingId}/ecs?${cacheBust}`);
                    setIsLoading(false);
                    setData(ecsBuilding);
                }
                catch (err) {
                    setErrorMessage(t('SERVER_ERROR'));
                    setIsLoading(false);
                }
            })();
        }
    }, [ buildingId, isLoading, data, t, errorMessage, setErrorMessage, setIsLoading, setData ]);

    useEffect(() => {
        return () => setData(undefined);
    }, [ buildingId, setData ]);

    const isEcsEnabled =
        data
            ? true
            : isLoading
                ? undefined
                : false;

    const isSubscriptionExpired =
        data
            ? data.enabledUntil < Date.now()
            : undefined;

    const daysUntilExpiry =
        data
            ? Math.floor((data.enabledUntil - Date.now()) / 86400000)
            : undefined;
    
    return {
        isEcsEnabled,
        isSubscriptionExpired,
        daysUntilExpiry,
        ecsBuilding: data,
        isLoadingEcsBuilding: isLoading,
        isSavingEcsBuilding: isSaving,
        saveEcsBuilding,
        ecsErrorMessage: errorMessage,
        successVisible,
        hideSuccess,
    };
}
